import React from 'react';
import { Text, NumberValue } from '../../typography';
import styled, { useTheme } from 'styled-components/macro';
import get from 'lodash.get';
import { SIZE } from '../../types';
import { IconWrapper, IconChevron } from '../../icons';
import { SPACINGS } from '../../utils';

const DropdownTableContainer = styled.table`
  width: 100%;
  table-layout: fixed;
  border-spacing: 0;
`;

const TableRow = styled.tr`
  & > td:first-child {
    padding-left: ${SPACINGS[6]};

    > div {
      display: flex;
      align-items: center;

      @media (max-width: 768px) {
        justify-content: space-between;
      }
    }
  }
  & > td:last-child {
    padding-right: ${SPACINGS[6]};
  }
`;

const TableHeaders = styled.tr`
  td:first-child {
    padding-left: ${SPACINGS[6]};
  }
  td:last-child {
    padding-right: ${SPACINGS[6]};
  }
`;

const TableHeader = styled.td`
  text-align: ${(props) => props.align || 'left'};
  padding: ${SPACINGS[2]};
`;

const TableCell = styled.td`
  text-align: ${(props) => props.align || 'left'};
  padding: ${SPACINGS[2]};
`;

const DropdownContainer = styled.div`
  width: 100%;
  background-color: inherit;
`;

type FormattedCellProps = {
  type: string;
  item: Record<string, any>;
  accessor: string;
  size: SIZE;
  currency?: string;
  style?: React.CSSProperties;
};
const FormattedCell = React.memo((props: FormattedCellProps) => {
  const { type, item, accessor, currency, style, size } = props;

  const itemSize = item.size ?? size;

  if (type === 'percentage') {
    return (
      <NumberValue
        style={style}
        value={get(item, accessor)}
        type="percentage"
        size={itemSize}
      />
    );
  }

  if (type === 'day') {
    return (
      <NumberValue
        style={style}
        value={get(item, accessor)}
        type="day"
        size={itemSize}
      />
    );
  }

  if (type === 'currency') {
    return (
      <NumberValue
        style={style}
        value={get(item, accessor)}
        type="currency"
        size={itemSize}
        currency={currency}
      />
    );
  }

  return (
    <Text size={itemSize} style={style} margin="none">
      {get(item, accessor)}
    </Text>
  );
});

type DropdownTableProps = {
  headers: Record<string, any>[];
  data: Record<string, any>[];
  dropdowns?: (Record<string, any> | null)[];
  headersSize?: SIZE;
  dataSize?: SIZE;
  style?: React.CSSProperties;
};
function DropdownTable(props: DropdownTableProps) {
  const {
    headers,
    data,
    dropdowns,
    headersSize = 'base',
    dataSize = 'base',
    style,
  } = props;
  const theme = useTheme();

  const [openedRows, setOpenedRows] = React.useState(() =>
    data.map(() => ({ open: false }))
  );

  React.useEffect(() => {
    setOpenedRows(data.map(() => ({ open: false })));
  }, [data]);

  function toggle(index: number) {
    setOpenedRows((oldOpenedRows) => {
      const newRowsState = [...oldOpenedRows];
      if (newRowsState[index]) {
        newRowsState[index].open = !newRowsState[index].open;
      }
      return newRowsState;
    });
  }

  function handleClick(index: number) {
    if (dropdowns && dropdowns[index]) {
      toggle(index);
    }
  }

  function isDropdownPresent(index: number) {
    return dropdowns && dropdowns[index];
  }

  function selectColor(index: number) {
    return index % 2 === 1 ? theme.colors.black95w : 'transparent';
  }

  return (
    <DropdownTableContainer style={style}>
      <tbody>
        <TableHeaders>
          {headers.map(({ title, align }) => (
            <TableHeader align={align} key={title}>
              <Text margin="none" type="label" size={headersSize}>
                {title}
              </Text>
            </TableHeader>
          ))}
        </TableHeaders>
        {data.map((item, dataIndex) => (
          <React.Fragment key={dataIndex}>
            <TableRow style={{ backgroundColor: selectColor(dataIndex) }}>
              {headers.map(
                (
                  { accessor, type, align, currency, style: headerStyle },
                  propIndex
                ) => (
                  <TableCell align={align} key={propIndex}>
                    {typeof accessor === 'function' || type === 'raw' ? (
                      accessor(item)
                    ) : (
                      <div>
                        <FormattedCell
                          size={dataSize}
                          type={type}
                          accessor={accessor}
                          item={item}
                          currency={currency}
                          style={
                            typeof headerStyle === 'function'
                              ? headerStyle(item)
                              : headerStyle
                          }
                        />
                        {isDropdownPresent(dataIndex) && propIndex === 0 && (
                          <IconWrapper                          
                            style={{ 
                              marginLeft: SPACINGS[2],
                              minWidth: SPACINGS[8],
                              minHeight: SPACINGS[8],
                            }}
                            onClick={() => handleClick(dataIndex)}
                            backgroundColor={
                              openedRows[dataIndex] &&
                              openedRows[dataIndex]?.open
                                ? theme.colors.primary95w
                                : theme.colors.primary95w
                              } 
                          >
                            <IconChevron 
                              stroke={theme.colors.primary}
                              direction={
                                openedRows[dataIndex] &&
                                openedRows[dataIndex]?.open
                                  ? 'top'
                                  : 'bottom'
                              }
                            />
                          </IconWrapper>
                        )}
                      </div>
                    )}
                  </TableCell>
                )
              )}
            </TableRow>
            {dropdowns && openedRows[dataIndex]?.open && (
              <tr style={{ backgroundColor: selectColor(dataIndex) }}>
                <td colSpan={headers.length}>
                  <DropdownContainer>{dropdowns[dataIndex]}</DropdownContainer>
                </td>
              </tr>
            )}
          </React.Fragment>
        ))}
      </tbody>
    </DropdownTableContainer>
  );
}

export default React.memo(DropdownTable);
